/**
 * 案件番号を画面に出すときの表記。
 *
 * 画面が案件番号を出す場所は 9 つあり（右上の案件バッジとその補足・業務マップの札・案件一覧・
 * レーンの見出し・業務フローの見出しと削除の注記・業務詳細の注記・削除された節点の
 * 注記・左右比較の変更後の札）、以前はその 3 通りがばらばらだった。素の数（`7`）を出す場所、
 * 前置きを 6 桁に 0 で埋めて出す場所（`AIRCLOSET-000007`）、前置きを翻訳文へ直に書いた場所
 * （`AIRCLOSET-{{pjNumber}}`）である。同じ案件が画面によって別の綴りで出ると、Backlog や
 * Slack で受け取った番号と目で突き合わせられない。
 *
 * **0 で埋めない。** 保存されている案件番号は数であり（本番の実測は `5` / `6` / `7` /
 * `139162`）、桁を足した綴りはどこにも保存されていない。Backlog の課題キーも 0 で埋めない
 * （実測: `AIRCLOSET-139162`、`keyId` は `139162`）。設計書が確定値として置いている綴りも
 * `AIRCLOSET-12345` である。桁を揃えるために 0 を足すと、画面だけが実在しない綴りを名乗る。
 *
 * **前置きを翻訳文へ書かない。** 翻訳文に `AIRCLOSET-` を書くと、言語を足すたびに綴りの規則が
 * 増え、1 言語だけ古い形で残っても型もテストも通る。翻訳文は埋める穴だけを持ち、綴りはここが
 * 決める。
 *
 * **前置きの綴りは契約の 1 つを使う**（`PJ_KEY_PREFIX` / `pjKeyOf`）。API は同じ規則で案件の憲章を
 * 引く鍵（Backlog の課題キー）を組むので、画面にだけ別の綴りを持つと、画面の表記と憲章の鍵が黙って
 * ずれる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件番号を、画面のどこでも同じ前置きつきの綴りへ直す。
 * @graph-connects pjKeyOf [calls] 案件番号から前置きつきの課題キーを組む契約の規則
 */
import { PJ_KEY_PREFIX, pjKeyOf } from '@cortex/domain-spec-console';

/**
 * 案件番号を画面に出す綴り（`AIRCLOSET-<番号>`）へ直す。
 *
 * 数と綴りの両方を受けるのは、案件番号の持ち方が場所によって違うからである。読取と契約は数で
 * 持ち（`ElementPjNumberSchema`）、URL の `?pj=` は文字列で届き、その文字列は前置きつきの形
 * （`AIRCLOSET-139162`）でも来る。受け口ごとに呼び分けを書くと、片方だけ古いまま残っても
 * 型が通る。
 *
 * **綴りの先頭に 0 が付いていたら剥がす。** URL の `?pj=007` は読取（`elementPjNumberOf`）が
 * `Number('007') = 7` として受理するので、中身は案件 7 のまま画面が開く。剥がさないと、右上の
 * 案件バッジだけが `AIRCLOSET-007` という実在しない綴りを名乗り、この関数が無くそうとしている
 * 「画面によって綴りが割れる」状態を 1 経路だけ残すことになる。数へ通さず綴りのまま削るのは、
 * 桁数に上限を置かないためである（`Number` を挟むと極端に長い綴りで精度が落ちる）。
 *
 * **数として読めない綴りは、そのまま返す。** 画面に出す値なので、読めないことを理由に落とすと
 * 「案件を見ているのに番号の欄が空」という、取得の失敗と見分けの付かない姿になる。空文字も
 * そのまま空文字で返るので、案件なしの場所（業務詳細の注記）は今までどおり何も出さない。
 *
 * @param pj 案件番号。数、素の綴り、前置きつきの綴りのいずれか
 * @returns 画面に出す案件番号。数として読めなければ受け取った綴りのまま
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件番号の数や綴りを、前置きつきの画面表記へ直す。
 * @graph-connects pjKeyOf [calls] 案件番号から前置きつきの課題キーを組む契約の規則
 */
export function formatPjLabel(pj: number | string): string {
  if (typeof pj === 'number') return pjKeyOf(pj);
  const trimmed = pj.trim();
  const digits = trimmed.startsWith(PJ_KEY_PREFIX) ? trimmed.slice(PJ_KEY_PREFIX.length) : trimmed;
  if (!/^\d+$/u.test(digits)) return pj;
  return `${PJ_KEY_PREFIX}${digits.replace(/^0+(?=\d)/u, '')}`;
}
